import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Copy, Download, Link2, AlertTriangle, ArrowRight, MessageCircle, BadgePercent, Star, Sparkles, ExternalLink, Info } from "lucide-react";
import { toast } from "sonner";
import { api, media } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { fmtNum, fmtPrice, vTitle, vDesc, SITE, copyText, OFFICIAL_PHONE, colorL } from "@/lib/format";
import { dict } from "@/lib/dict";
import { VehicleGrid } from "@/components/xd/VehicleCard";
import { PageTitle, Loader, Empty, RestrictionChips, Section, Stat, StatusBadge } from "@/components/xd/ui";
import { AssistantSection } from "@/components/xd/AssistantSection";
import { PhotoSaver, singleDownloadUrl } from "@/components/xd/PhotoSaver";
import { useFilters, FilterBar } from "@/pages/public/Inventory";
import { SpecGrid } from "@/pages/public/VehicleDetail";
import { GuideBody, GuideAccept } from "@/pages/dealer/Dealer";

export const MAX_MARKUP = 10000;
const WA_NUMBER = OFFICIAL_PHONE.replace(/\D/g, "");

export const discountMessage = (v, uid, l) => {
  const d = dict[l];
  return [d["discount.msg_hello"], `${d["discount.msg_broker"]}: ${uid}`, `${d["discount.msg_vehicle"]}: ${vTitle(v, l)}`, `${d["discount.msg_code"]}: ${v.code}`,
    `${d["discount.msg_price"]}: ${fmtPrice(v.price)}`, "", d["discount.msg_request"]].join("\n");
};

const BrokerPricing = ({ v, uid }) => {
  const { t, lang } = useLang();
  const href = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(discountMessage(v, uid, lang))}`;
  return (
    <Section title={t("bprice.title")} testid="broker-pricing-section">
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="xd-panel p-4 border-t-2 border-t-[var(--xd-red)]"><p className="text-xs text-zinc-400">{t("bprice.official")}</p><p className="text-xl ltr-num chrome-text font-semibold mt-1" data-testid="broker-official-price">{fmtPrice(v.price)}</p></div>
        <div className="xd-panel p-4 border-t-2 border-t-zinc-500"><p className="text-xs text-zinc-400">{t("bprice.max")}</p><p className="text-xl ltr-num mt-1" data-testid="broker-max-price">{v.price ? fmtPrice(v.price + MAX_MARKUP) : "—"}</p></div>
      </div>
      <ul className="text-xs text-zinc-400 space-y-1.5 list-disc ps-5"><li>{t("bprice.rule1")}</li><li>{t("bprice.rule2")}</li><li>{t("bprice.rule3")}</li></ul>
      <a href={href} target="_blank" rel="noreferrer" className="btn-ghost" data-testid="broker-discount-request-button"><BadgePercent size={16} /><MessageCircle size={16} />{t("discount.button")}</a>
      <p className="text-[11px] text-zinc-600">{t("discount.note")}</p>
    </Section>
  );
};

export const refLink = (uid, code) => `${SITE}/r/${uid}${code ? `/${code}` : ""}`;

export const CopyButton = ({ text, label, testid, primary, className = "" }) => {
  const { t } = useLang();
  return (
    <button className={`${primary ? "btn-xd" : "btn-ghost"} ${className}`} data-testid={testid}
      onClick={async () => { (await copyText(text)) ? toast.success(t("common.copied")) : toast.error(t("err.generic")); }}>
      <Copy size={16} />{label}
    </button>
  );
};

const useFavorites = () => {
  const { t, tErr } = useLang();
  const [ids, setIds] = useState(new Set());
  useEffect(() => { api.get("/broker/favorites/ids").then((r) => setIds(new Set(r.data.ids))).catch(() => {}); }, []);
  const toggle = async (id) => {
    const on = ids.has(id);
    try {
      await (on ? api.delete(`/broker/favorites/${id}`) : api.put(`/broker/favorites/${id}`));
      const n = new Set(ids); on ? n.delete(id) : n.add(id); setIds(n);
      toast.success(t(on ? "fav.removed" : "fav.added"));
    } catch (e) { toast.error(tErr(e)); }
  };
  return { ids, toggle };
};

const StarButton = ({ on, onToggle, code }) => {
  const { t } = useLang();
  return (
    <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggle(); }} aria-pressed={on} aria-label={t(on ? "fav.remove" : "fav.add")}
      className={`h-10 w-10 rounded-full grid place-items-center backdrop-blur-md border transition-colors ${on ? "bg-black/70 border-amber-400/70 text-amber-400" : "bg-black/50 border-white/20 text-white hover:border-white/50"}`}
      data-testid={`fav-star-${code}`} data-on={on}>
      <Star size={18} fill={on ? "currentColor" : "none"} />
    </button>
  );
};

export function BrokerVehicles() {
  const { t } = useLang();
  const { user } = useAuth();
  const { f, set, params, reset } = useFilters();
  const fav = useFavorites();
  const [items, setItems] = useState(null);
  const key = JSON.stringify(params);
  useEffect(() => {
    api.get("/broker/vehicles", { params: JSON.parse(key) }).then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, [key]);
  return (
    <div>
      <PageTitle eyebrow={t("broker.portal")} title={t("broker.vehicles")} />
      <div className="xd-panel p-5 mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4" data-testid="broker-id-card">
        <div>
          <p className="eyebrow">{t("broker.your_id")}</p>
          <p className="mt-2 text-2xl font-semibold chrome-text ltr-num" data-testid="broker-uid">{user.uid}</p>
          <p className="mt-1 text-xs text-zinc-500 ltr-num break-all" data-testid="broker-ref-link">{refLink(user.uid)}</p>
        </div>
        <CopyButton text={refLink(user.uid)} label={t("broker.copy_link")} testid="broker-copy-general-link" />
      </div>
      <FilterBar f={f} set={set} facets={{}} reset={reset} count={items?.length ?? 0} availabilityView brandProps={{ url: "/broker/brands", labelKey: "broker.vehicles", allKey: "bnav.all_publishable" }} />
      {items && <p className="text-sm text-zinc-500 -mt-4 mb-6" data-testid="broker-result-count">{t("bnav.count", { n: items.length })}</p>}
      {!items ? <Loader /> : items.length ? (
        <VehicleGrid items={items} linkFor={(v) => `/broker/v/${v.code}`} cornerFor={(v) => <StarButton on={fav.ids.has(v.id)} onToggle={() => fav.toggle(v.id)} code={v.code} />} />
      ) : <Empty text={t("inventory.empty")} />}
    </div>
  );
}

const buildText = (v, uid, l) => {
  const d = dict[l];
  const title = vTitle(v, l);
  return [
    title,
    `${v.year} · ${fmtNum(v.mileage)} ${d["unit.km"]} · ${d[`fuel.${v.fuel}`]} · ${d[`gear.${v.gearbox}`]} · ${d["spec.hand"]} ${v.hand}`,
    `${d["spec.color"]}: ${colorL(v.color, l)}${v.interior_color ? ` · ${d["spec.interior_color"]}: ${colorL(v.interior_color, l)}` : ""}`,
    `${d["vehicle.price"]}: ${fmtPrice(v.price)}`,
    "",
    vDesc(v, l),
    "",
    `${d["broker.copy_more"]} ${refLink(uid, v.code)}`,
  ].join("\n");
};

export function BrokerVehicle() {
  const { code } = useParams();
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const fav = useFavorites();
  const [v, setV] = useState(null);
  const [textLang, setTextLang] = useState(lang);
  useEffect(() => {
    api.get(`/broker/vehicles/${code}`).then((r) => setV(r.data)).catch(() => navigate("/broker"));
  }, [code, navigate]);
  if (!v) return <Loader />;
  const blocked = Object.entries(v.restrictions).filter(([, ok]) => !ok);
  const sold = v.availability === "sold";
  return (
    <div className="space-y-6">
      <Link to="/broker/vehicles" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-white" data-testid="broker-back-link"><ArrowRight size={16} />{t("broker.vehicles")}</Link>
      <PageTitle eyebrow={v.code} title={vTitle(v, lang)}>
        <div className="flex items-center gap-3"><StarButton on={fav.ids.has(v.id)} onToggle={() => fav.toggle(v.id)} code={`detail-${v.code}`} /><p className="text-2xl chrome-text font-semibold ltr-num">{fmtPrice(v.price)}</p></div>
      </PageTitle>
      {sold && <p className="border border-red-600/60 bg-red-950/30 text-red-200 text-sm p-4 flex items-start gap-2" data-testid="broker-sold-banner"><AlertTriangle size={16} className="mt-0.5 shrink-0" />{t("fav.sold_detail")}</p>}
      {v.availability === "reserved" && <p className="border border-amber-500/50 bg-amber-950/20 text-amber-200 text-sm p-4 flex items-start gap-2" data-testid="broker-reserved-banner"><Info size={16} className="mt-0.5 shrink-0" />{t("fav.reserved_detail")}</p>}
      <SpecGrid v={v} />
      {sold ? null : <>
      <BrokerPricing v={v} uid={user.uid} />
      <Section title={t("broker.restrictions")} testid="broker-restrictions">
        {blocked.length > 0 && <p className="text-sm text-red-300 flex items-center gap-2" data-testid="broker-restrictions-warning"><AlertTriangle size={16} />{t("broker.restrictions_warn")}</p>}
        <RestrictionChips restrictions={v.restrictions} />
      </Section>
      <Section title={t("broker.copy_details")} testid="broker-copy-section">
        <div className="flex gap-2">{["he", "ar"].map((l) => (
          <button key={l} onClick={() => setTextLang(l)} data-testid={`copy-lang-${l}`} className={`px-4 py-2 text-sm border ${textLang === l ? "border-[var(--xd-red)] text-white" : "border-white/10 text-zinc-500"}`}>{l === "he" ? "עברית" : "العربية"}</button>
        ))}</div>
        <pre className="whitespace-pre-wrap text-sm text-zinc-300 bg-black border border-white/10 p-4 font-[inherit] max-h-72 overflow-auto" data-testid="broker-copy-preview">{buildText(v, user.uid, textLang)}</pre>
        <div className="flex flex-wrap gap-3">
          <CopyButton primary text={buildText(v, user.uid, textLang)} label={t("broker.copy_text")} testid="broker-copy-text-button" />
          <CopyButton text={refLink(user.uid, v.code)} label={t("broker.copy_vehicle_link")} testid="broker-copy-vehicle-link" />
        </div>
      </Section>
      <Section title={t("broker.photos", { n: v.photos.length })} testid="broker-photos-section">
        <PhotoSaver photos={v.photos} code={v.code} title={vTitle(v, lang)} />
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
          {v.photos.map((p, i) => (
            <div key={p.id} className="relative group">
              <img src={media(p.id, "thumb")} alt="" className="aspect-square w-full object-cover" loading="lazy" />
              <a href={singleDownloadUrl(p.id)} download className="absolute bottom-1 end-1 h-9 w-9 grid place-items-center bg-black/75 border border-white/15" data-testid={`broker-photo-download-${i}`} title={t("broker.download_original")}><Download size={15} /></a>
            </div>
          ))}
        </div>
      </Section>
      </>}
    </div>
  );
}

const FavCard = ({ v, onRemove }) => {
  const { t, lang } = useLang();
  if (v.unavailable) {
    return (
      <div className="xd-card p-5 space-y-3" data-testid={`fav-card-${v.code || v.id}`}>
        <p className="text-sm text-zinc-300"><span className="ltr-num">{v.code || "—"}</span> · {t("fav.unavailable")}</p>
        <button className="btn-ghost w-full" onClick={onRemove} data-testid={`fav-remove-${v.code || v.id}`}><Star size={15} />{t("fav.remove")}</button>
      </div>
    );
  }
  const sold = v.availability === "sold";
  const note = sold ? t("fav.sold_note") : v.availability === "reserved" ? t("fav.reserved_note") : null;
  return (
    <div className="xd-card min-w-0 flex flex-col" data-testid={`fav-card-${v.code}`}>
      <Link to={`/broker/v/${v.code}`} className="block" data-testid={`fav-open-${v.code}`}>
        <div className="relative aspect-[16/10] bg-[#111] overflow-hidden">
          {v.photos?.[0] && <img src={media(v.photos[0].id, "thumb")} alt="" className={`h-full w-full object-cover ${sold ? "grayscale opacity-60" : ""}`} />}
          <StatusBadge status={v.availability} className="absolute top-3 start-3" />
        </div>
        <div className="p-5 space-y-1.5">
          <p className="font-medium line-clamp-2 break-words">{vTitle(v, lang)}</p>
          <p className="text-xs text-zinc-500"><span className="ltr-num">{v.year}</span> · <span className="ltr-num">{v.code}</span></p>
          <p className={`text-lg ltr-num font-semibold ${sold ? "text-zinc-500 line-through" : "chrome-text"}`} data-testid={`fav-price-${v.code}`}>{fmtPrice(v.price)}</p>
        </div>
      </Link>
      <div className="mt-auto px-5 pb-5 space-y-3">
        {note && <p className={`text-xs leading-relaxed p-3 border ${sold ? "border-red-600/50 bg-red-950/20 text-red-200" : "border-amber-500/40 bg-amber-950/20 text-amber-200"}`} data-testid={`fav-note-${v.code}`}>{note}</p>}
        <button className="btn-ghost w-full !min-h-[2.75rem] !text-sm" onClick={onRemove} data-testid={`fav-remove-${v.code}`}><Star size={15} fill="currentColor" className="text-amber-400" />{t("fav.remove")}</button>
      </div>
    </div>
  );
};

export function BrokerFavorites() {
  const { t, tErr } = useLang();
  const [items, setItems] = useState(null);
  const load = () => api.get("/broker/favorites").then((r) => setItems(r.data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const remove = async (id) => { try { await api.delete(`/broker/favorites/${id}`); toast.success(t("fav.removed")); load(); } catch (e) { toast.error(tErr(e)); } };
  return (
    <div className="space-y-6" data-testid="broker-favorites-page">
      <PageTitle eyebrow={t("broker.portal")} title={t("fav.title")} />
      <div className="xd-panel p-4 md:p-5 border-s-2 border-s-amber-400 space-y-1.5" data-testid="fav-guidance">
        <p className="text-sm font-medium flex items-center gap-2"><Star size={15} className="text-amber-400" />{t("fav.guide_title")}</p>
        <p className="text-sm text-zinc-400 leading-relaxed">{t("fav.guide_text")}</p>
        <p className="text-xs text-zinc-500 leading-relaxed">{t("fav.not_reservation")}</p>
      </div>
      {!items ? <Loader /> : items.length ? (
        <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">{items.map((v) => <FavCard key={v.id} v={v} onRemove={() => remove(v.id)} />)}</div>
      ) : <Empty text={t("fav.empty")} />}
    </div>
  );
}

export function BrokerStats() {
  const { t } = useLang();
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/broker/stats").then((r) => setS(r.data)); }, []);
  if (!s) return <Loader />;
  return (
    <div className="space-y-8">
      <PageTitle eyebrow={t("broker.portal")} title={t("bnav.stats")} />
      <div className="grid sm:grid-cols-3 gap-4">
        <Stat label={t("stats.referral_visits")} value={s.referral_visits} testid="stat-referral-visits" />
        <Stat label={t("stats.whatsapp_clicks")} value={s.whatsapp_clicks} testid="stat-whatsapp-clicks" />
        <Stat label={t("stats.callbacks")} value={s.callbacks} testid="stat-callbacks" />
      </div>
      <Section title={t("stats.by_vehicle")}>
        {s.by_vehicle.length ? (
          <div className="overflow-x-auto"><table className="xd-table"><thead><tr><th>{t("common.vehicle")}</th><th>{t("stats.referral_visits")}</th><th>{t("stats.whatsapp_clicks")}</th><th>{t("stats.callbacks")}</th></tr></thead>
            <tbody>{s.by_vehicle.map((r) => <tr key={r.code}><td>{r.title} <span className="text-zinc-500 ltr-num text-xs">{r.code}</span></td><td className="ltr-num">{r.referral_visit || 0}</td><td className="ltr-num">{r.whatsapp_click || 0}</td><td className="ltr-num">{r.callback || 0}</td></tr>)}</tbody></table></div>
        ) : <p className="text-sm text-zinc-500">{t("stats.none")}</p>}
      </Section>
    </div>
  );
}

export function BrokerTerms() {
  const { t } = useLang();
  return (
    <div className="space-y-6 max-w-3xl" data-testid="commission-terms-page">
      <PageTitle eyebrow={t("broker.portal")} title={t("terms.title")} />
      <Section>
        <ul className="space-y-4 text-zinc-300 leading-relaxed list-disc ps-5">
          {[1, 2, 3, 4, 5].map((n) => <li key={n}>{t(`terms.p${n}`)}</li>)}
        </ul>
        <div className="xd-line" />
        <div className="text-sm text-zinc-400 space-y-1" data-testid="commission-example">
          <p className="eyebrow mb-2">{t("terms.example")}</p>
          <p>{t("terms.ex1")}</p><p>{t("terms.ex2")}</p><p>{t("terms.ex3")}</p><p className="text-white">{t("terms.ex4")}</p>
        </div>
      </Section>
    </div>
  );
}

const BROKER_GUIDE = [["restrictions", 3], ["pricing", 5], ["identity", 2], ["confidentiality", 3], ["referral", 3], ["commission", 3]];

export function BrokerRules() {
  const { t } = useLang();
  const { user } = useAuth();
  const accepted = user.guide_version === user.guide_current;
  return (
    <div className="space-y-6 max-w-3xl" data-testid="broker-rules-page">
      <PageTitle eyebrow={t("broker.portal")} title={t("bguide.title")} />
      <p className="text-sm text-zinc-400 flex items-start gap-2"><Link2 size={16} className="mt-0.5 shrink-0" />{t("bguide.intro")}</p>
      {accepted && <AssistantSection role="broker" />}
      <GuideBody prefix="bguide" sections={BROKER_GUIDE} testid="broker-guide" />
      <GuideAccept home="/broker" testid="rules" />
    </div>
  );
}
